import {
  Campaign,
  CampaignConfiguration,
  CampaignType,
  DiscountConfig,
} from 'apis/restaurant-api/merchant-admin/campaigns/types';

import { DiscountedItem } from '../hooks/useDiscountedItems';
import { CampaignListItem, CampaignOption, CampaignStatus } from '../types';

export const createCampaign = (overrides: Partial<Campaign> = {}): Campaign => {
  return {
    id: 'test-id',
    campaign_type: CampaignType.DELIVERY_DISCOUNT,
    name: 'Test Campaign Name',
    description: 'Some campaign description lorem ipsum dolor sit amet consectetur',
    start_date: new Date().toISOString(),
    end_date: new Date().toISOString(),
    terminated: false,
    total_budget_sum: 32100,
    per_user_usage_limit: 123,
    first_purchase_only: false,
    total_spent_budget: 1,
    created_by: 'test-user',
    venue_ids: [],
    min_basket_size: 2000,
    max_delivery_distance: 1,
    basket_discount: {
      amount: 200,
      percentage: 5,
    },
    delivery_discount: {
      amount: 300,
      percentage: 5,
    },
    item_discount: {
      item_trigger_count: 2,
      target_item: {
        item_id: 'test-item-id',
        venue_id: 'test-venue-id',
      },
      amount: null,
      percentage: null,
      free_items_count: 1,
    },
    weekly_schedule: {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    },
    sponsor_type: 'WOLT',
    budget_type: 'WOLT',
    created_at: new Date().toISOString(),
    created_by_wolt: false,
    currency: 'EUR',
    ...overrides,
  };
};

export const createCampaignOption = (overrides: Partial<CampaignOption> = {}): CampaignOption => ({
  tag: 'test-tag',
  title: 'Test Campaign Title',
  description: 'Test Campaign Description',
  image: 'test-image',
  type: CampaignType.DELIVERY_DISCOUNT,
  ...overrides,
});

export const createDiscountConfig = (overrides: Partial<DiscountConfig> = {}): DiscountConfig => ({
  amounts: [1000, 2000, 3000, 4000],
  percentages: [10, 15, 20, 30],
  recommended_amount: 2000,
  recommended_percentage: 15,
  upper_limits: [200, 400, 600, 800],
  ...overrides,
});

export const createCampaignConfiguration = (overrides: Partial<CampaignConfiguration> = {}): CampaignConfiguration => ({
  campaign_types: Object.values(CampaignType),
  max_delivery_distances: [1000, 2000, 5000],
  basket_discounts: {
    1000: createDiscountConfig(),
    2000: createDiscountConfig(),
    5000: createDiscountConfig(),
  },
  delivery_discounts: {
    1000: createDiscountConfig(),
    2000: createDiscountConfig(),
    5000: createDiscountConfig(),
  },
  default_total_budget_sum: 321,
  item_discount: {
    percentage: {
      min_percentage: 5,
      max_percentage: 50,
    },
  },
  promotion_per_venue_limit: 15,
  ...overrides,
});

export const mockItemTranslation = (value: string) => [{ lang: 'en' as const, value, verified: true }];

export const createItem = (overrides: Partial<DiscountedItem> = {}): DiscountedItem => ({
  name: 'Test Item',
  description: 'Test Item Description',
  additives: [],
  alcohol_percentage: 0,
  allergens: [],
  availability_period: { start: null, end: null },
  baseprice: { amount: '10', currency: 'EUR' },
  brand_id: null,
  caffeine_content: null,
  conditions_of_use_and_storage: [],
  country_of_origin: [],
  courier_restrictions: [],
  delivery_methods: [],
  deposit_permutation_id: null,
  dietary_preferences: [],
  discounted: null,
  display_unit: 'units',
  distributor_information: [],
  source_config: null,
  enabled: true,
  barcode_gtin: null,
  external_id: null,
  forbidden_courier_capabilities: [],
  id: 'item-1234',
  image_blurhash: null,
  image: null,
  images: [],
  ingredients: [],
  inventory_mode: null,
  is_bundle_offer: false,
  mandatory_warnings: [],
  menu_id: 'menu-1234',
  merchant_sku: null,
  more_information: [],
  number_of_units: 1,
  nutrition_facts: [],
  nutrition_values: [],
  nutrition_values_baseline: null,
  option_bindings: [],
  producer_information: [],
  product_hierarchy_level_id: null,
  required_courier_capabilities: [],
  user_requirements: [],
  vat_percentage: '24',
  vat_category_code: 'FIN-1',
  version: {
    number: 1, // eslint-disable-line id-blacklist
    created_at: { $date: 1582113825740 },
    author: { id: 'ipad_client_foobar', kind: 'merchant_app' },
  },
  volume_in_ml: null,
  weekly_availability: null,
  weekly_visibility: null,
  weight_in_grams: null,
  max_quantity_per_purchase: null,
  min_quantity_per_purchase: null,
  sell_by_weight_config: null,
  external_info_hyperlink: null,
  restrictions: [],
  wolt_plus_only: false,
  return_policy: null,
  category: { id: 'test-category', name: 'Test' },
  ...overrides,
});

export const TestIds = {
  FORM: {
    WRAPPER: 'campaign-form',
    DISCOUNT_TYPE: {
      WRAPPER: 'campaign-form__discount-type-section',
    },
    DISCOUNTED_ITEM: {
      REMOVE: 'campaign-form__discounted-item-section__remove',
    },
    DISCOUNT_AMOUNT: {
      WRAPPER: 'campaign-form__discount-amount-section',
      NOTE: 'campaign-form__discount-amount-section__note',
    },
    RULES: {
      WRAPPER: 'campaign-form__rules-section',
    },
    BUDGET: {
      WRAPPER: 'campaign-form__budget-section',
    },
    DELIVERY_RANGE: {
      WRAPPER: 'campaign-form__delivery-range-section',
    },
    SCHEDULING: {
      WRAPPER: 'campaign-form__scheduling-section',
    },
    AGREEMENTS: {
      WRAPPER: 'campaign-form__agreements-section',
      AGRREMENT_TERMS_AND_CONDITIONS: 'campaign-form__agreements-section__terms-and-conditions',
    },
    ID: {
      WRAPPER: 'campaign-form__id-section',
    },
  },
  DETAILS: {
    DISCOUNT_TYPE: {
      WRAPPER: 'campaign-details__discount-type-section',
    },
    RULES: {
      WRAPPER: 'campaign-details__rules-section',
    },
    DISCOUNT_AMOUNT: {
      WRAPPER: 'campaign-details__discount-amount-section',
    },
    BUDGET: {
      WRAPPER: 'campaign-details__budget-section',
    },
    SCHEDULING: {
      WRAPPER: 'campaign-details__scheduling-section',
    },
    AGREEMENTS: {
      WRAPPER: 'campaign-details__agreements-section',
      AGREEMENT: 'campaign-details__agreements-section__agreement',
      SPINNER: 'campaign-details__agreements-section__spinner',
    },
    ID: {
      WRAPPER: 'campaign-details__id-section',
    },
  },
};

export const createCampaignListItem = (overrides: Partial<CampaignListItem> = {}): CampaignListItem => {
  return {
    campaignId: 'test-id',
    campaignType: CampaignType.DELIVERY_DISCOUNT,
    status: CampaignStatus.RUNNING,
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    budget: 32100,
    isCreatedByWolt: false,
    paidBy: 'WOLT',

    allowedActions: ['read', 'edit', 'terminate', 'clone'],
    venueIds: [],
    ...overrides,
  };
};

export const createAdCampaignListItem = (overrides: Partial<CampaignListItem> = {}): CampaignListItem => {
  return {
    campaignId: 'test-id',
    campaignType: CampaignType.ADS,
    status: CampaignStatus.RUNNING,
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    budget: 32100,
    creationSource: 'merchantAdmin',
    paidBy: 'WOLT',
    allowedActions: ['read', 'edit', 'stop'],
    venueIds: [],
    ...overrides,
  };
};
